import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { ArrowLeft, Home } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { appConfig } from "../../lib/config/appConfig";
import { getBackgroundTask, subscribeBackgroundTask, type BackgroundTask } from "../../lib/backgroundTaskStore";
import { hrisAccessFor, screenKeyForPath, type HrisAccess } from "../../modules/hris/hrisAccess";
import { PresenceBar } from "../components/PresenceBar";
import { canOpenAuditLog } from "../../modules/mfg-workcenters/audit/auditAccess";
import { SidebarFooterLine } from "../components/SidebarTicker";
import { navIconFor } from "./navIcons";

interface NavItem {
  path: string;
  label: string;
  icon: string;
  enabled: boolean;
  end?: boolean;
  /**
   * Extra paths this entry owns.
   *
   * NavLink lights up from `to` alone, so an entry that fronts a two-tab screen
   * (Availability & Board) goes dark the moment you switch to the other tab —
   * the nav claiming you are nowhere while you are standing on its own screen.
   */
  alsoActiveFor?: string[];
}

// Mirrors the side nav from your existing SPFx dashboard. Disabled items will
// light up as each module gets migrated in Phase 2.
const WORKSPACE_ITEMS: NavItem[] = [
  { path: "/crm", label: "CRM Geomapping", icon: "", enabled: true, end: true },
  { path: "/crm/historical", label: "Historical Data", icon: "", enabled: true },
  { path: "/crm/b2b", label: "B2B Pipeline", icon: "", enabled: true },
  { path: "/crm/feedback", label: "Customer Feedback", icon: "", enabled: true },
  { path: "/crm/embroidery", label: "Embroidery Request", icon: "", enabled: true },
];

/**
 * E-COMMERCE STORE — the sales channels, one row each.
 *
 * Their own labelled group rather than three more rows under Workspace
 * (Mikko, 2026-09-20). They had been landing at the bottom of the CRM list as
 * each one was built, beside Geomapping and Customer Feedback, which are tools
 * rather than channels — so the list read as "CRM things, and then some
 * shops". A channel is a place the company SELLS, and there will be more of
 * them; a heading makes that a set somebody can scan rather than a tail that
 * keeps growing.
 *
 * ORDERED BY VOLUME, which is the order Mikko gave: TikTok, IG COM, Lazada.
 * Not alphabetical and not the order they were built — the busiest channel is
 * the one opened most often, and it should be the shortest reach.
 *
 * Lazada was listed here with `enabled: false` before it existed, so the plan
 * was visible in the nav rather than living only in a conversation. It opened
 * on 2026-09-21 by flipping that one boolean — which is the point of listing
 * a screen early: the nav never had to be redesigned to admit it.
 */
const ECOMMERCE_ITEMS: NavItem[] = [
  // FIRST, above the channels, because it is the only entry that answers a
  // question about all of them — and the one somebody opens every morning.
  // The channels below it are where you go to fix one thing; this is where
  // you go to find out what needs fixing.
  { path: "/crm/ecommerce", label: "Fulfilment & Performance", icon: "", enabled: true },
  { path: "/crm/tiktok", label: "TikTok Shop", icon: "", enabled: true },
  { path: "/crm/igcom", label: "IG COM", icon: "", enabled: true },
  { path: "/crm/lazada", label: "Lazada Shop", icon: "", enabled: true },
];

const MODULE_ITEMS: NavItem[] = [
  { path: "/hris", label: "HRIS · Payroll", icon: "", enabled: true },
  // Lands on Production Grid, matching the launcher tile and the first entry
  // in the module's own nav — three ways in, one destination.
  { path: "/manufacturing/production-grid", label: "Manufacturing", icon: "", enabled: true },
  // Lands on the payroll directory rather than the run: the run is the weekly
  // action, but the directory is what somebody arriving cold needs first, and
  // a run against an incomplete roster is the one that costs a week's pay.
  { path: "/subcontractors", label: "Subcontractors", icon: "", enabled: true },
  { path: "/sewer", label: "Subcontractor Sewers", icon: "", enabled: true },
  { path: "/rmc", label: "RMC", icon: "", enabled: true },
  { path: "/inventory/rmc", label: "Inventory · RMC", icon: "", enabled: true, end: true },
  { path: "/accounting/sales-invoice", label: "Accounting", icon: "", enabled: true },
  { path: "/accounting/bills-registry", label: "Bills & Subscriptions", icon: "", enabled: true },
  { path: "/accounting/bookkeeping", label: "Bookkeeping", icon: "", enabled: true },
  { path: "/accounting/daily-expenses", label: "Daily Expenses", icon: "", enabled: true },
  { path: "/outdoor/expenses", label: "Outdoor Activity", icon: "", enabled: true },
];

interface FocusNavSection {
  label: string;
  items: NavItem[];
}

// Modules that bring their own screens instead of jumping to other top-level
// modules render their sub-nav directly in THIS sidebar (one nav column
// total) rather than a second nav column inside the module itself.
const FOCUS_NAV: Record<string, FocusNavSection[]> = {
  // Fourteen flat entries was one undifferentiated list you had to read end to
  // end to find anything. These four groups follow the order the work happens
  // in: what is on the floor, who it goes to, how it went, and the machinery
  // behind it. Production Grid leads because it is the screen most sessions
  // start from.
  "/manufacturing": [
    {
      label: "Manufacturing",
      items: [
        { path: "/manufacturing/production-grid", label: "Production Grid", icon: "", enabled: true, end: true },
        // Smart Prod Plan is HIDDEN, not removed — it still runs on mock data
        // and is not ready to be found by someone looking for real numbers.
        // The route, the screen and its icon entry all stay, so restoring it is
        // this one line; /manufacturing/prodplan still works for anyone who has
        // it bookmarked or is working on it.
        // { path: "/manufacturing/prodplan", label: "Smart Prod Plan", icon: "", enabled: true },
        { path: "/manufacturing/work-orders", label: "Control Tower · BQC1", icon: "", enabled: true, end: true },
        { path: "/manufacturing/cutting", label: "Cutting · BQC2", icon: "", enabled: true },
        { path: "/manufacturing/dispatch", label: "Dispatch · BQC3", icon: "", enabled: true },
        { path: "/manufacturing/receiving", label: "Receiving · BQC4", icon: "", enabled: true },
        { path: "/manufacturing/qc", label: "QC · BQC5", icon: "", enabled: true },
        // Last in the group on purpose: it reads the five stations above it
        // and owns none of them.
        {
          path: "/manufacturing/monitoring", label: "Manu Monitoring", icon: "", enabled: true,
          alsoActiveFor: [
            "/manufacturing/monitoring/pending", "/manufacturing/monitoring/stores",
            "/manufacturing/monitoring/done", "/manufacturing/monitoring/import",
          ],
        },
      ],
    },
    {
      label: "Smart Assign",
      items: [
        // ONE entry, because they are now one screen in two tabs (see
        // bqc1/Planning.tsx). Listing both kept them looking like two places to
        // go, which is exactly what merging them was meant to stop; the other
        // tab is one click away and always visible from either side.
        //
        // It lands on Availability, not the Board: it is the gate everything
        // downstream reads, and arriving at the board first is how a planner
        // ends up assigning against last week's commitments.
        {
          path: "/manufacturing/work-orders/availability", label: "Availability & Board",
          icon: "", enabled: true, alsoActiveFor: ["/manufacturing/work-orders/board"],
        },
        { path: "/manufacturing/work-orders/sewers", label: "Sewer Intelligence", icon: "", enabled: true },
        { path: "/manufacturing/work-orders/what-if", label: "What-If Simulator", icon: "", enabled: true },
      ],
    },
    {
      label: "Analysis",
      items: [
        { path: "/manufacturing/work-orders/subcontractors", label: "Subcontractors", icon: "", enabled: true },
        { path: "/manufacturing/work-orders/metrics", label: "Sewer Metrics", icon: "", enabled: true },
        { path: "/manufacturing/work-orders/roster", label: "Scoring Diagnostics", icon: "", enabled: true },
        { path: "/manufacturing/work-orders/gate1", label: "Gate 1 Report", icon: "", enabled: true },
      ],
    },
    {
      label: "Tools",
      items: [
        { path: "/manufacturing/work-orders/valve", label: "Migration Valve", icon: "", enabled: true },
        { path: "/manufacturing/fulfillment-handover", label: "Fulfillment Handover", icon: "", enabled: true },
        { path: "/manufacturing/fulfillment-source-sync", label: "Fulfillment Source Sync", icon: "", enabled: true },
      ],
    },
    {
      label: "Settings",
      items: [
        { path: "/manufacturing/production-grid/settings", label: "Grid Settings", icon: "", enabled: true },
        { path: "/manufacturing/work-orders/settings", label: "Workcenter Settings", icon: "", enabled: true },
        // Who used the app and for how long. Listed for everybody in this array and
        // removed per-account by isAllowed below — it is a record of colleagues'
        // activity, so accounts that may not open it do not see it exists.
        { path: "/manufacturing/audit-log", label: "Audit Log", icon: "", enabled: true },
        // Who may sign in to the sewer app, and publishing what each sewer sees (was Sewer Portal Admin).
        { path: "/manufacturing/sewer-portal", label: "Sewer Portal", icon: "", enabled: true },
      ],
    },
  ],
  "/outdoor": [
    {
      label: "Outdoor Activity",
      items: [
        { path: "/outdoor/expenses", label: "Event Expenses", icon: "", enabled: true },
        { path: "/outdoor/sales", label: "Sales Income", icon: "", enabled: true },
      ],
    },
  ],
  "/accounting": [
    {
      label: "Cash",
      items: [
        { path: "/accounting/daily-expenses", label: "Daily Expenses", icon: "", enabled: true },
      ],
    },
    {
      label: "Accounts Payable",
      items: [
        { path: "/accounting/ap", label: "AP · Overview", icon: "", enabled: true, end: true },
        { path: "/accounting/ap/pr", label: "Purchase Requisition", icon: "", enabled: true },
        { path: "/accounting/ap/po", label: "Purchase Order", icon: "", enabled: true },
        { path: "/accounting/ap/grn", label: "Goods Receipt Note", icon: "", enabled: true },
        { path: "/accounting/ap/invoice", label: "Supplier's Invoice", icon: "", enabled: true },
        { path: "/accounting/ap/matching", label: "A/P Matching", icon: "", enabled: true },
        { path: "/accounting/ap/pv", label: "Payment Voucher", icon: "", enabled: true },
        { path: "/accounting/ap/or", label: "Official Receipt", icon: "", enabled: true },
      ],
    },
    {
      label: "Accounts Receivable",
      items: [
        { path: "/accounting/ar", label: "AR · Overview", icon: "", enabled: true, end: true },
        { path: "/accounting/ar/so", label: "Sales Order", icon: "", enabled: true },
        { path: "/accounting/ar/dr", label: "Delivery Receipt", icon: "", enabled: true },
        { path: "/accounting/ar/si", label: "Sales Invoice", icon: "", enabled: true },
        { path: "/accounting/ar/recording", label: "A/R Recording", icon: "", enabled: true },
        { path: "/accounting/ar/collection", label: "Collection (OR)", icon: "", enabled: true },
        { path: "/accounting/ar/deposit", label: "Deposit Slip", icon: "", enabled: true },
      ],
    },
    {
      label: "Books & Records",
      items: [
        { path: "/accounting/bookkeeping", label: "Bookkeeping", icon: "", enabled: true },
        { path: "/accounting/sales-invoice", label: "Sales Invoice Booklet", icon: "", enabled: true },
        { path: "/accounting/bills-registry", label: "Bills & Subscriptions", icon: "", enabled: true },
      ],
    },
    {
      label: "System",
      items: [
        { path: "/accounting/settings", label: "Settings", icon: "", enabled: true },
      ],
    },
  ],
  "/hris": [
    {
      label: "HR & Payroll",
      items: [
        { path: "/hris/computation", label: "Payroll Computation", icon: "", enabled: true },
        { path: "/hris/payroll", label: "Payroll Workflow", icon: "", enabled: true },
        { path: "/hris/bankfile", label: "Bank File", icon: "", enabled: true },
        { path: "/hris/benefits", label: "Mandatory Benefits", icon: "", enabled: true },
        { path: "/hris/thirteenth", label: "13th Month Pay", icon: "", enabled: true },
        { path: "/hris/periods", label: "Weekly Payroll", icon: "", enabled: true },
        { path: "/hris/payslips", label: "Payslips", icon: "", enabled: true },
        { path: "/hris/reports", label: "Reports", icon: "", enabled: true },
      ],
    },
    {
      label: "People",
      items: [
        { path: "/hris/employees", label: "Employees", icon: "", enabled: true },
        { path: "/hris/201", label: "201 File", icon: "", enabled: true },
        { path: "/hris/attendance", label: "Attendance", icon: "", enabled: true },
        { path: "/hris/attendance-summary", label: "Attendance Summary", icon: "", enabled: true },
        { path: "/hris/scoreboard", label: "KPI Scoreboard", icon: "", enabled: true },
        { path: "/hris/leaves", label: "Leaves & SIL", icon: "", enabled: true },
        { path: "/hris/leave-credits", label: "Leave Credits", icon: "", enabled: true },
        { path: "/hris/leave-recon", label: "Leave Recon", icon: "", enabled: true },
        { path: "/hris/loans", label: "Loans", icon: "", enabled: true },
      ],
    },
    {
      label: "Reference",
      items: [
        { path: "/hris/leave-types", label: "Leave Types & Credits", icon: "", enabled: true },
        { path: "/hris/holidays", label: "Holidays & Day Types", icon: "", enabled: true },
        { path: "/hris/schedules", label: "Work Schedules", icon: "", enabled: true },
      ],
    },
    {
      label: "Accounting Firm",
      items: [
        { path: "/hris/workbook", label: "Monthly Payroll Workbook", icon: "", enabled: true },
        { path: "/hris/1601c", label: "BIR 1601-C", icon: "", enabled: true },
      ],
    },
    {
      label: "System",
      items: [
        { path: "/hris/roles", label: "Roles & Users", icon: "", enabled: true },
        { path: "/hris/settings", label: "Settings", icon: "", enabled: true },
        { path: "/hris/discover", label: "Discovery", icon: "", enabled: true },
      ],
    },
  ],
  // The sewers' own module: only its own screens.
  "/sewer": [
    {
      label: "My work",
      items: [
        { path: "/sewer", label: "Despatch Slip", icon: "", enabled: true, end: true },
        { path: "/sewer/scan", label: "Scan & Declare", icon: "", enabled: true },
        { path: "/sewer/dr", label: "Delivery Receipt", icon: "", enabled: true },
        { path: "/sewer/status", label: "Status", icon: "", enabled: true },
      ],
    },
    {
      label: "Pay",
      items: [
        { path: "/sewer/payout", label: "Payout", icon: "", enabled: true },
      ],
    },
    {
      label: "Account",
      items: [
        { path: "/sewer/me", label: "Me", icon: "", enabled: true },
      ],
    },
  ],
  "/subcontractors": [
    {
      label: "Payroll",
      items: [
        { path: "/subcontractors/payroll", label: "Subcon Payroll", icon: "", enabled: true },
        { path: "/subcontractors/records", label: "Paid history", icon: "", enabled: true },
        { path: "/subcontractors/bizlink", label: "Bank File", icon: "", enabled: true },
      ],
    },
    {
      label: "Reference",
      items: [
        { path: "/subcontractors", label: "Directory", icon: "", enabled: true, end: true },
        { path: "/subcontractors/rates", label: "Rate Card", icon: "", enabled: true },
      ],
    },
    {
      label: "System",
      items: [
        { path: "/subcontractors/settings", label: "Settings", icon: "", enabled: true },
      ],
    },
  ],
  // Their ERP has 84 screens. These are the ones ported so far, and the
  // group is named after their own page so the two read the same.
  "/fulfillment": [
    {
      label: "Ecommerce",
      items: [
        { path: "/fulfillment/order-database", label: "Order Database", icon: "", enabled: true },
      ],
    },
  ],
  "/inventory/rmc": [
    {
      label: "Views",
      items: [
        { path: "/inventory/rmc", label: "Inventory", icon: "", enabled: true, end: true },
        { path: "/inventory/rmc/generate", label: "Generate Labels", icon: "", enabled: true },
      ],
    },
    {
      label: "Scanning",
      items: [
        { path: "/inventory/rmc/inbound", label: "Inbound", icon: "", enabled: true },
        { path: "/inventory/rmc/outbound", label: "Outbound", icon: "", enabled: true },
      ],
    },
    {
      label: "History",
      items: [
        { path: "/inventory/rmc/activity", label: "Activity", icon: "", enabled: true },
        { path: "/inventory/rmc/awaiting-inbound", label: "Awaiting Inbound", icon: "", enabled: true },
        { path: "/inventory/rmc/reconciliation", label: "Reconciliation", icon: "", enabled: true },
      ],
    },
    {
      label: "Reports",
      items: [
        { path: "/inventory/rmc/dashboard", label: "Dashboard", icon: "", enabled: true },
        { path: "/inventory/rmc/reorder-planning", label: "Reorder Planning", icon: "", enabled: true },
        { path: "/inventory/rmc/alerts", label: "Alerts", icon: "", enabled: true },
      ],
    },
    {
      label: "Tools",
      items: [
        { path: "/inventory/rmc/register-item", label: "Register Item", icon: "", enabled: true },
        { path: "/inventory/rmc/masterfile", label: "Master File", icon: "", enabled: true },
        { path: "/inventory/rmc/suppliers", label: "Suppliers", icon: "", enabled: true },
        { path: "/inventory/rmc/cycle-count", label: "Cycle Count", icon: "", enabled: true },
        { path: "/inventory/rmc/stock-adjustment", label: "Stock Adjustment", icon: "", enabled: true },
        { path: "/inventory/rmc/clear-test-data", label: "Clear Test Data", icon: "", enabled: true },
      ],
    },
  ],
};

/** Some modules hold confidential data — hide the nav link for non-allowed
 * accounts. HRIS is per-screen (payroll privacy); Bills is whole-module. */
function isAllowed(path: string, email: string | undefined, hris: HrisAccess): boolean {
  if (path.startsWith("/hris")) {
    // Accounting-firm exports carry full payroll detail — owner/finance only.
    if (path === "/hris/settings" || path === "/hris/discover"
        || path === "/hris/workbook" || path === "/hris/1601c"
        || path === "/hris/roles"
        || path === "/hris/201") return hris.full;
    // The holiday calendar is the reference behind attendance and holiday pay,
    // so it follows Attendance access.
    if (path === "/hris/holidays" || path === "/hris/schedules") return hris.screens.has("attendance");
    if (path === "/hris/leave-types" || path === "/hris/leave-credits" || path === "/hris/leave-recon") return hris.screens.has("leaves");
    const key = screenKeyForPath(path);
    return key ? hris.screens.has(key) : hris.hasAny;
  }
  if (path === "/manufacturing/audit-log") return canOpenAuditLog(email);
  if (path !== "/accounting/bills-registry") return true;
  const allowed = appConfig.billsRegistryAllowedUsers;
  if (allowed.length === 0) return true;
  return !!email && allowed.includes(email.toLowerCase());
}

function sectionLabel(pathname: string): string {
  if (pathname.startsWith("/manufacturing")) return "Manufacturing";
  if (pathname.startsWith("/crm/b2b")) return "B2B Pipeline";
  if (pathname.startsWith("/crm/historical")) return "Historical Data";
  if (pathname.startsWith("/crm/embroidery")) return "Embroidery Request";
  if (pathname.startsWith("/crm/feedback")) return "Customer Feedback";
  if (pathname.startsWith("/crm/igcom")) return "IG COM";
  if (pathname.startsWith("/crm/tiktok")) return "TIKTOK SHOP";
  if (pathname.startsWith("/crm/lazada")) return "LAZADA SHOP";
  if (pathname.startsWith("/crm/ecommerce")) return "E-COMMERCE";
  if (pathname.startsWith("/crm")) return "CRM";
  if (pathname.startsWith("/hris")) return "HRIS · Payroll";
  if (pathname.startsWith("/fulfillment")) return "Fulfillment";
  if (pathname === "/sewer" || pathname.startsWith("/sewer/")) return "Subcontractor Sewers";
  if (pathname.startsWith("/subcontractors")) return "Subcontractors";
  if (pathname.startsWith("/inventory")) return "Inventory · RMC";
  if (pathname.startsWith("/rmc")) return "RMC";
  if (pathname.startsWith("/accounting")) return "Accounting";
  if (pathname.startsWith("/outdoor")) return "Outdoor Activity";
  return "Home";
}

interface SidebarProps {
  /** On mobile the sidebar is an off-canvas drawer; this controls its visibility. */
  mobileOpen?: boolean;
  /** Called when the user picks a nav destination, so the mobile drawer can close. */
  onNavigate?: () => void;
}

export function Sidebar({ mobileOpen = false, onNavigate }: SidebarProps = {}) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const email = accounts[0]?.username;
  const hris = hrisAccessFor(email);
  const visibleModuleItems = MODULE_ITEMS.filter((item) =>
    // The module-level HRIS link is gated on having ANY HRIS screen, not on the
    // Dashboard specifically — an executive granted only the KPI Scoreboard
    // still needs the module entry to reach it. HrisGuard redirects /hris to
    // their first allowed screen.
    item.path === "/hris" ? hris.hasAny : isAllowed(item.path, email, hris)
  );
  // Focus mode: while inside a module with its own screens (so far just
  // Inventory · RMC), swap the workspace/other-modules list for that
  // module's own nav sections — one nav column total, Back/Home still work.
  const focusPrefix = Object.keys(FOCUS_NAV).find((prefix) => pathname.startsWith(prefix));
  return (
    <aside className={`sidebar ${mobileOpen ? "sidebar-mobile-open" : ""}`}>
      <div className="sidebar-brand">
        <div className="sidebar-brand-logo">iS</div>
        <div className="sidebar-brand-text">
          <div className="sidebar-brand-name">i-SYNERGY</div>
          <div className="sidebar-brand-sub">{sectionLabel(pathname)}</div>
        </div>
      </div>

      <div className="sidebar-nav-controls">
        <button
          className="module-nav-btn"
          onClick={() => {
            onNavigate?.();
            navigate(-1);
          }}
          title="Go back"
        >
          <ArrowLeft size={14} strokeWidth={2.5} /> Back
        </button>
        <button
          className="module-nav-btn"
          onClick={() => {
            onNavigate?.();
            navigate("/");
          }}
          title="Back to launcher"
        >
          <Home size={14} strokeWidth={2.5} /> Home
        </button>
      </div>

      <BackgroundTaskBanner />

      {focusPrefix ? (
        <nav className="sidebar-nav">
          {FOCUS_NAV[focusPrefix]
            .map((section) => ({ section, items: section.items.filter((item) => isAllowed(item.path, email, hris)) }))
            .filter(({ items }) => items.length > 0)
            .map(({ section, items }, i) => (
              <div key={section.label}>
                <div className={`sidebar-section-title ${i > 0 ? "sidebar-section-spaced" : ""}`}>{section.label}</div>
                {items.map((item) => (
                  <NavItem key={item.path} item={item} onNavigate={onNavigate} />
                ))}
              </div>
            ))}
        </nav>
      ) : (
        <nav className="sidebar-nav">
          <div className="sidebar-section-title">Workspace</div>
          {WORKSPACE_ITEMS.map((item) => (
            <NavItem key={item.path} item={item} onNavigate={onNavigate} />
          ))}

          <div className="sidebar-section-title sidebar-section-spaced">E-Commerce Store</div>
          {ECOMMERCE_ITEMS.map((item) => (
            <NavItem key={item.path} item={item} onNavigate={onNavigate} />
          ))}

          <div className="sidebar-section-title sidebar-section-spaced">Other Modules</div>
          {visibleModuleItems.map((item) => (
            <NavItem key={item.path} item={item} onNavigate={onNavigate} />
          ))}
        </nav>
      )}

      {/* Who else is on the app, directly above your own name. Lives here
          rather than floating in the top-right corner, where it sat over
          whatever controls each screen puts there. */}
      <PresenceBar />

      <SidebarUser />

      <div className="sidebar-footer">
        {/* The running text from the Home settings, or — blank — the version and
            build time that used to sit here (it also stays in the tooltip). */}
        <SidebarFooterLine
          stamp={`${appConfig.appName} · v${appConfig.appVersion}${appConfig.builtAt ? ` · ${appConfig.builtAt}` : ""}`}
        />
      </div>
    </aside>
  );
}

/**
 * Persistent banner for a background task (e.g. Sales Invoice import) that
 * keeps running after the user navigates to another module — the Sidebar
 * never unmounts, so this stays visible no matter which module is active.
 */
function BackgroundTaskBanner() {
  const [task, setTask] = useState<BackgroundTask | null>(() => getBackgroundTask());
  useEffect(() => subscribeBackgroundTask(setTask), []);
  if (!task) return null;
  const pct = task.total > 0 ? Math.min(100, Math.round((task.done / task.total) * 100)) : 0;
  return (
    <div className="sidebar-bg-task" title={`${task.done} / ${task.total}`}>
      <div className="sidebar-bg-task-label">
        {task.label}… <b>{pct}%</b>
      </div>
      <div className="sidebar-bg-task-track">
        <div className="sidebar-bg-task-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/**
 * The row's icon. Decorative — the label beside it already names the screen,
 * so it is hidden from assistive tech rather than read out twice. A path with
 * no icon yet falls back to whatever the nav entry carried, and renders
 * nothing if that is empty too.
 */
function NavIcon({ path, fallback }: { path: string; fallback?: string }) {
  const Icon = navIconFor(path);
  if (Icon) {
    return (
      <span className="sidebar-item-icon" aria-hidden="true">
        <Icon size={15} strokeWidth={1.9} />
      </span>
    );
  }
  return fallback ? <span className="sidebar-item-icon" aria-hidden="true">{fallback}</span> : null;
}

function NavItem({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  const { pathname } = useLocation();
  if (!item.enabled) {
    return (
      <div className="sidebar-item sidebar-item-disabled" title="Not yet migrated">
        <NavIcon path={item.path} fallback={item.icon} />
        <span className="sidebar-item-label">{item.label}</span>
        <span className="sidebar-item-badge">Soon</span>
      </div>
    );
  }

  return (
    <NavLink
      to={item.path} end={item.end}
      onClick={() => onNavigate?.()}
      className={({ isActive }) =>
        `sidebar-item ${isActive || (item.alsoActiveFor ?? []).some(p => pathname.startsWith(p))
          ? "sidebar-item-active" : ""}`
      }
    >
      <NavIcon path={item.path} fallback={item.icon} />
      <span className="sidebar-item-label">{item.label}</span>
    </NavLink>
  );
}

/**
 * User profile pill at the bottom of the sidebar.
 * Shows avatar + name; clicking opens a menu with email + sign out.
 * Replaces the old TopBar's user menu.
 */
function SidebarUser() {
  const { instance, accounts } = useMsal();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const account = accounts[0];

  // Close the menu when clicking outside
  useEffect(() => {
    if (!menuOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [menuOpen]);

  const handleSignOut = () => {
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin
    });
  };

  const initials = account?.name
    ? account.name
        .split(" ")
        .map((part) => part[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : "?";

  return (
    <div className="sidebar-user" ref={menuRef}>
      <button
        className="sidebar-user-button"
        onClick={() => setMenuOpen((open) => !open)}
        aria-label="User menu"
      >
        <span className="sidebar-user-avatar">{initials}</span>
        <span className="sidebar-user-name">{account?.name ?? account?.username ?? "User"}</span>
      </button>

      {menuOpen && (
        <div className="sidebar-user-menu">
          <div className="sidebar-user-menu-info">
            <div className="sidebar-user-menu-name">{account?.name}</div>
            <div className="sidebar-user-menu-email">{account?.username}</div>
          </div>
          <button className="sidebar-user-menu-item" onClick={handleSignOut}>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
